import ApiRateLimitBanner from 'components/ApiRateLimitBanner';
import ConfirmActionModal from 'components/common/ConfirmActionModal';
import { PackAndFocusEditorDrawer } from 'components/pack-and-focus-editor';
import PackPreviewDrawer from 'components/pack-focus-picker/PackPreviewDrawer';
import MoreOptionsModalRevamp from 'components/setting-generator-revamp/tabs-revamp/more-options/MoreOptionsModalRevamp';
import { LOCAL_STORAGE, TIMEOUT_DELAY, TOAST } from 'constants/general';
import { PARAMS } from 'constants/params';
import { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { getCurrentPlatformFont, getApiLanguageCode } from 'utils/helpers';
import { embeddedPageLayoutSelector } from 'store/reducer/selectors/navigation-routes.selector';
import {
  configureTabs,
  updateDeviceID,
  updateIsTokenAddedToInterceptor
} from 'store/reducer/setting/slice';
import {
  Mode,
  NATIVE_TOKEN_REQUEST_OUTCOME,
  NATIVE_TOKEN_REQUEST_REASON,
  WEBVIEW_LOADING_STATUS
} from 'enums';
import { WEBVIEW_ROUTES } from 'constants/routes';
import { AUTH_EVENT } from 'constants/events';
import { EmbeddedAuthFailedDetail } from 'interfaces/bridge.interface';
import { getLocalStorage } from 'utils/helpers/storage';
import { isAccessTokenValid } from 'utils/auth-token-validation.util';
import {
  getSyncedExternalPlatforms,
  getUserFocusModes,
  getUserSummary,
  getUserTimezone
} from 'store/reducer/user/extra';
import { isCurrentPath, getQueryParam } from 'utils/routes.util';
import { isPublicWebviewMarketplace } from 'utils/marketplace.util';
import {
  isEmbeddedNativeContext,
  isNativeTokenRequestInProgress,
  requestAccessTokenFromNative
} from 'bridge/native-auth.bridge';
import InitEmbeddedServices from 'dashboard/InitEmbeddedServices';
import {
  trackEmbeddedInitStart,
  sendReady,
  sendTimeout,
  sendError,
  isNativeReadyTransportAvailable
} from 'utils/webviewProtocol';
import {
  getLibraryActivities,
  getUserSettings
} from 'store/reducer/setting/extra';
import { getTutorials } from 'store/reducer/course/extra';
import {
  getHabitPacks,
  getUserInstalledHabitPacks
} from 'store/reducer/habit-pack/extra';
import ErrorBoundaryFallback from 'pages/other/ErrorBoundaryFallback';
import RefreshFab from 'components/ui/RefreshFab';
import { generateCorrelationId } from 'utils/error.util';
import Container from 'components/ui/Container';
import { t } from 'services/i18n.service';
import { toast } from 'react-toastify';
import { EMBEDDED_AUTH_ERROR_MESSAGE_KEY } from 'constants/error.constant';
import { useWindowEventListener } from 'hooks/useWindowEventListener';

interface EmbeddedErrorState {
  status: WEBVIEW_LOADING_STATUS;
  error: Error | null;
  correlationId: string | null;
  messageKey?: string;
}

// Frames (~1s at 60fps) to wait for a native readiness transport before sending READY
// regardless, so a shell that attaches late still gets the signal without blocking.
const MAX_READY_TRANSPORT_FRAMES = 60;

const createEmbeddedAuthErrorState = (
  outcome: NATIVE_TOKEN_REQUEST_OUTCOME
): EmbeddedErrorState => {
  const isTimeout = outcome === NATIVE_TOKEN_REQUEST_OUTCOME.TIMEOUT;

  return {
    status: isTimeout
      ? WEBVIEW_LOADING_STATUS.TIMEOUT
      : WEBVIEW_LOADING_STATUS.ERROR,
    error: new Error(outcome),
    correlationId: generateCorrelationId(),
    messageKey: isTimeout
      ? EMBEDDED_AUTH_ERROR_MESSAGE_KEY.TIMEOUT
      : EMBEDDED_AUTH_ERROR_MESSAGE_KEY.FAILED
  };
};

const EmbeddedPageLayout = ({ children }: PropsWithChildren) => {
  const dispatch = useAppDispatch();
  const [embeddedErrorState, setEmbeddedErrorState] =
    useState<EmbeddedErrorState>({
      status: WEBVIEW_LOADING_STATUS.INITIALIZING,
      error: null,
      correlationId: null
    });
  // Reactive so a client-side webview nav (window.fbNavigate) re-runs the per-screen
  // init effects — re-requesting the token and re-emitting the readiness signal.
  const { pathname: screen } = useLocation();
  const readySentRef = useRef(false);

  const {
    font,
    platform,
    showConfirmModal,
    isTokenAddedToInterceptor,
    language
  } = useAppSelectorShallow(embeddedPageLayoutSelector);

  const currentFont = useMemo(
    () => getCurrentPlatformFont(platform, font),
    [platform, font]
  );
  const {
    status,
    error: initError,
    correlationId,
    messageKey
  } = embeddedErrorState;

  // On client-side webview navigation, reset readiness so the incoming screen re-arms its
  // init timeout and emits a fresh READY signal. Declared before the init effects so
  // readySentRef is cleared before they run in the same effect flush.
  useEffect(() => {
    readySentRef.current = false;
    setEmbeddedErrorState((prev) =>
      prev.status === WEBVIEW_LOADING_STATUS.INITIALIZING
        ? prev
        : {
            status: WEBVIEW_LOADING_STATUS.INITIALIZING,
            error: null,
            correlationId: null
          }
    );
  }, [screen]);

  useEffect(() => {
    trackEmbeddedInitStart(screen);

    const routine = getQueryParam(PARAMS.EDIT_SPECIFIC_ROUTINE);
    const deviceId = getQueryParam(PARAMS.DEVICE_ID);
    deviceId && dispatch(updateDeviceID(deviceId));
    routine && dispatch(configureTabs({ mode: Mode.CUSTOM, routine }));

    const token = getLocalStorage(LOCAL_STORAGE.AUTH_BEARER_TOKEN);
    if (isAccessTokenValid(token)) {
      dispatch(updateIsTokenAddedToInterceptor(true));
    } else if (token && isEmbeddedNativeContext()) {
      void requestAccessTokenFromNative(NATIVE_TOKEN_REQUEST_REASON.EXPIRED);
    } else if (isEmbeddedNativeContext()) {
      void requestAccessTokenFromNative(NATIVE_TOKEN_REQUEST_REASON.MISSING);
    }
  }, [dispatch, screen]);

  useWindowEventListener(AUTH_EVENT.EMBEDDED_NETWORK_UNAVAILABLE, () => {
    toast.error(t(EMBEDDED_AUTH_ERROR_MESSAGE_KEY.NETWORK));
  });

  useWindowEventListener(
    AUTH_EVENT.EMBEDDED_AUTH_FAILED,
    (event) => {
      const { outcome } = (event as CustomEvent<EmbeddedAuthFailedDetail>)
        .detail;
      const errorState = createEmbeddedAuthErrorState(outcome);

      setEmbeddedErrorState(errorState);

      if (
        errorState.status === WEBVIEW_LOADING_STATUS.TIMEOUT &&
        errorState.correlationId
      ) {
        sendTimeout(screen, { correlationId: errorState.correlationId });
        return;
      }

      if (errorState.error && errorState.correlationId) {
        sendError(screen, {
          error: errorState.error,
          correlationId: errorState.correlationId
        });
      }
    },
    [screen]
  );

  useEffect(() => {
    if (!isTokenAddedToInterceptor) {
      return;
    }

    setEmbeddedErrorState((prev) =>
      prev.status === WEBVIEW_LOADING_STATUS.INITIALIZING
        ? { ...prev, status: WEBVIEW_LOADING_STATUS.LOADING }
        : prev
    );

    let cancelled = false;

    // Emit READY once a native transport is listening (RN mobile bridge OR Mac
    // webview_ready handler). Bounded retry: the Mac has no ReactNativeWebView, so we
    // cannot wait on RN specifically — after a short grace period we send anyway so
    // PostHog and any attached shell still receive the signal.
    let framesWaited = 0;
    const sendReadyWhenAvailable = () => {
      if (cancelled || readySentRef.current) {
        return;
      }

      if (
        isNativeReadyTransportAvailable() ||
        framesWaited >= MAX_READY_TRANSPORT_FRAMES
      ) {
        readySentRef.current = true;
        setEmbeddedErrorState((prev) => ({
          ...prev,
          status: WEBVIEW_LOADING_STATUS.READY
        }));
        sendReady(screen);
        return;
      }

      framesWaited += 1;
      requestAnimationFrame(sendReadyWhenAvailable);
    };

    // Double rAF ensures React has committed the screen before we declare it ready.
    requestAnimationFrame(() => {
      requestAnimationFrame(sendReadyWhenAvailable);
    });

    const runInitialization = async () => {
      try {
        const promises: Promise<unknown>[] = [];
        promises.push(
          dispatch(getUserSummary()).then(() => dispatch(getUserTimezone())),
          dispatch(getSyncedExternalPlatforms())
        );
        if (isCurrentPath(WEBVIEW_ROUTES.WEBVIEW_STATS)) {
          promises.push(dispatch(getUserSettings()));
        }
        if (isCurrentPath(WEBVIEW_ROUTES.WEBVIEW_SETTINGS)) {
          promises.push(
            dispatch(getUserSettings()),
            dispatch(getTutorials()),
            dispatch(getLibraryActivities()),
            dispatch(getUserInstalledHabitPacks(false)),
            dispatch(
              getHabitPacks({
                language: getApiLanguageCode(language),
                marketplace_approval_status: true,
                is_featured: true
              })
            ),
            dispatch(getUserFocusModes())
          );
        }
        if (isCurrentPath(WEBVIEW_ROUTES.WEBVIEW_ROUTINE_SUGGESTION_REVAMP)) {
          promises.push(
            dispatch(getLibraryActivities()),
            dispatch(
              getHabitPacks({
                language: getApiLanguageCode(language),
                marketplace_approval_status: true,
                is_featured: true
              })
            )
          );
        }

        Promise.allSettled(promises);
      } catch (error) {
        if (cancelled) return;
        const normalizedError =
          error instanceof Error ? error : new Error(String(error));
        const newCorrelationId = generateCorrelationId();
        setEmbeddedErrorState({
          status: WEBVIEW_LOADING_STATUS.ERROR,
          error: normalizedError,
          correlationId: newCorrelationId
        });
        sendError(screen, {
          error: normalizedError,
          correlationId: newCorrelationId
        });
      }
    };

    runInitialization();

    return () => {
      cancelled = true;
    };
  }, [dispatch, isTokenAddedToInterceptor, language, screen]);

  useEffect(() => {
    if (isPublicWebviewMarketplace() || isTokenAddedToInterceptor) {
      return;
    }

    if (status !== WEBVIEW_LOADING_STATUS.INITIALIZING) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      if (readySentRef.current || isNativeTokenRequestInProgress()) {
        return;
      }

      const timeoutError = new Error('WEBVIEW_INITIALIZATION_TIMEOUT');
      const newCorrelationId = generateCorrelationId();
      setEmbeddedErrorState({
        status: WEBVIEW_LOADING_STATUS.TIMEOUT,
        error: timeoutError,
        correlationId: newCorrelationId
      });
      sendTimeout(screen, {
        correlationId: newCorrelationId
      });
    }, TIMEOUT_DELAY.WEBVIEW_INITIALIZATION);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [status, screen, isTokenAddedToInterceptor]);

  if (
    [WEBVIEW_LOADING_STATUS.TIMEOUT, WEBVIEW_LOADING_STATUS.ERROR].includes(
      status
    )
  ) {
    return (
      <ErrorBoundaryFallback
        error={initError}
        correlationId={correlationId}
        messageKey={messageKey}
      />
    );
  }

  const embeddedPageContent = (
    <main
      className={`w-full h-full flex flex-col ${currentFont} overflow-hidden max-w-[120rem] mx-auto bg-secondary-100 dark:bg-secondary-dark-100 text-secondary-dark-50 dark:text-secondary-50`}>
      <ApiRateLimitBanner />
      <div className='flex-1 min-h-0 overflow-hidden flex flex-col'>
        {isCurrentPath(WEBVIEW_ROUTES.WEBVIEW_SETTINGS) ? (
          <Container
            size='small'
            className='w-full h-full overflow-y-auto scrollbar-thin-custom'>
            {children}
          </Container>
        ) : (
          children
        )}
      </div>
      {showConfirmModal && <ConfirmActionModal />}
      <PackAndFocusEditorDrawer />
      <PackPreviewDrawer />
      <MoreOptionsModalRevamp />
      <RefreshFab onRefresh={() => window.location.reload()} />
      <ToastContainer
        limit={TOAST.LIMIT}
        draggable={false}
        pauseOnHover={false}
        pauseOnFocusLoss={false}
        autoClose={TOAST.AUTO_CLOSE.DEFAULT}
        closeOnClick={true}
      />
    </main>
  );

  if (isPublicWebviewMarketplace()) {
    return embeddedPageContent;
  }

  if (!isTokenAddedToInterceptor) {
    return <InitEmbeddedServices />;
  }

  return embeddedPageContent;
};

export default EmbeddedPageLayout;
